import React, { useState } from "react";

import {
    Alert,
    Button,
    Card,
    Col,
    Row,
    Spin,
    Tag,
    Typography,
    message,
} from "antd";

import {
    ArrowLeftOutlined,
    BookOutlined,
    FileTextOutlined,
    RobotOutlined,
} from "@ant-design/icons";

import api from "../api/api";

const {
    Title,
    Text,
    Paragraph,
} = Typography;


const CurriculumTopicDetails = ({
    theme,
    topic,
    onBack,
    onLessonEditor,
}) => {

    const [
        generatingPlan,
        setGeneratingPlan,
    ] = useState(false);

    const [
        generatingNote,
        setGeneratingNote,
    ] = useState(false);


    /*
    |--------------------------------------------------------------------------
    | No topic selected
    |--------------------------------------------------------------------------
    */

    if (!topic) {

        return (
            <div
                style={{
                    padding: 24,
                }}
            >

                <Alert
                    type="warning"
                    message="No curriculum topic selected"
                    description={
                        "Please return to the curriculum search "
                        + "and select a topic."
                    }
                />

                <Button
                    icon={<ArrowLeftOutlined />}
                    style={{
                        marginTop: 16,
                    }}
                    onClick={onBack}
                >
                    Back to Curriculum
                </Button>

            </div>
        );
    }


    /*
    |--------------------------------------------------------------------------
    | Topic ID
    |--------------------------------------------------------------------------
    */

    const topicId = topic.id;


    /*
    |--------------------------------------------------------------------------
    | Generate Lesson Plan
    |--------------------------------------------------------------------------
    */

    const generateLessonPlan = async () => {

        if (!topicId) {

            message.error(
                "This curriculum topic does not have a valid ID."
            );

            return;
        }


        setGeneratingPlan(true);


        try {

            const response = await api.post(
                "/api/curriculum/lesson-plans/generate/",
                {
                    topic_id: topicId,
                }
            );


            message.success(
                "Lesson plan generated successfully."
            );


            /*
            |--------------------------------------------------------------------------
            | Pass generated lesson plan to lesson editor
            |--------------------------------------------------------------------------
            */

            if (onLessonEditor) {

                onLessonEditor({
                    lessonPlan: response.data,
                    lessonNote: null,
                });
            }


        } catch (error) {

            console.error(
                "Lesson plan generation failed:",
                error
            );


            message.error(
                error.response?.data?.detail ||
                error.response?.data?.error ||
                "Unable to generate lesson plan."
            );

        } finally {

            setGeneratingPlan(false);
        }
    };


    /*
    |--------------------------------------------------------------------------
    | Generate Lesson Note
    |--------------------------------------------------------------------------
    */

    const generateLessonNote = async () => {

        if (!topicId) {

            message.error(
                "This curriculum topic does not have a valid ID."
            );

            return;
        }


        setGeneratingNote(true);


        try {

            const response = await api.post(
                "/api/curriculum/lesson-notes/generate/",
                {
                    topic_id: topicId,
                }
            );


            message.success(
                "Lesson note generated successfully."
            );


            /*
            |--------------------------------------------------------------------------
            | Pass generated lesson note to lesson editor
            |--------------------------------------------------------------------------
            */

            if (onLessonEditor) {

                onLessonEditor({
                    lessonPlan: null,
                    lessonNote: response.data,
                });
            }


        } catch (error) {

            console.error(
                "Lesson note generation failed:",
                error
            );


            message.error(
                error.response?.data?.detail ||
                error.response?.data?.error ||
                "Unable to generate lesson note."
            );

        } finally {

            setGeneratingNote(false);
        }
    };


    /*
    |--------------------------------------------------------------------------
    | Normalize related objects
    |--------------------------------------------------------------------------
    */

    const unit = topic.unit || {};

    const subject =
        topic.subject ||
        unit.subject ||
        (
            topic.subject_name
                ? {
                    name: topic.subject_name,
                }
                : null
        );


    const gradeClass =
        topic.grade_class ||
        unit.grade_class ||
        (
            topic.grade_class_name
                ? {
                    name: topic.grade_class_name,
                }
                : null
        );


    const term =
        topic.term ||
        unit.term ||
        (
            topic.term_name
                ? {
                    name: topic.term_name,
                }
                : null
        );


    const unitName =
        unit.name ||
        topic.unit_name;


    /*
    |--------------------------------------------------------------------------
    | Theme
    |--------------------------------------------------------------------------
    */

    const pageBackground =
        theme?.background ||
        "#f5f7fb";

    const cardBackground =
        theme?.card ||
        "#ffffff";

    const textColor =
        theme?.text ||
        "#172033";

    const secondaryColor =
        theme?.secondary ||
        "#64748b";

    const borderColor =
        theme?.border ||
        "#e5e7eb";


    /*
    |--------------------------------------------------------------------------
    | Render
    |--------------------------------------------------------------------------
    */

    return (

        <div
            style={{
                padding: 24,
                maxWidth: 1400,
                margin: "0 auto",
                background: pageBackground,
                color: textColor,
                minHeight: "100%",
            }}
        >

            {/* ------------------------------------------------------------- */}
            {/* BACK BUTTON */}
            {/* ------------------------------------------------------------- */}

            <Button
                icon={<ArrowLeftOutlined />}
                onClick={onBack}
                style={{
                    marginBottom: 20,
                }}
            >
                Back to Curriculum
            </Button>


            {/* ------------------------------------------------------------- */}
            {/* TOPIC HEADER */}
            {/* ------------------------------------------------------------- */}

            <Card
                style={{
                    borderRadius: 12,
                    marginBottom: 20,
                    background: cardBackground,
                    borderColor,
                }}
            >

                <Row gutter={[24, 24]}>

                    <Col
                        xs={24}
                        lg={16}
                    >

                        <SpaceBlock>

                            {subject && (
                                <Tag color="blue">
                                    {subject.name}
                                </Tag>
                            )}


                            {gradeClass && (
                                <Tag color="green">
                                    {gradeClass.name}
                                </Tag>
                            )}


                            {term && (
                                <Tag color="purple">
                                    {term.name}
                                </Tag>
                            )}

                        </SpaceBlock>


                        <Title
                            level={2}
                            style={{
                                marginTop: 14,
                                marginBottom: 8,
                                color: textColor,
                            }}
                        >
                            {topic.name}
                        </Title>


                        {unitName && (

                            <Text
                                style={{
                                    color: secondaryColor,
                                }}
                            >
                                Unit: {unitName}
                            </Text>

                        )}

                    </Col>


                    {/* ----------------------------------------------------- */}
                    {/* ACTION BUTTONS */}
                    {/* ----------------------------------------------------- */}

                    <Col
                        xs={24}
                        lg={8}
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: 10,
                            flexWrap: "wrap",
                        }}
                    >

                        <Button
                            type="primary"
                            icon={<RobotOutlined />}
                            loading={generatingPlan}
                            onClick={generateLessonPlan}
                        >
                            Generate Lesson Plan
                        </Button>


                        <Button
                            icon={<FileTextOutlined />}
                            loading={generatingNote}
                            onClick={generateLessonNote}
                        >
                            Generate Lesson Note
                        </Button>

                    </Col>

                </Row>

            </Card>


            {/* ------------------------------------------------------------- */}
            {/* CURRICULUM INFORMATION */}
            {/* ------------------------------------------------------------- */}

            <Row gutter={[20, 20]}>

                <Col
                    xs={24}
                    lg={16}
                >

                    <Card
                        title={
                            <span>
                                <BookOutlined
                                    style={{
                                        marginRight: 8,
                                    }}
                                />

                                Curriculum Content
                            </span>
                        }
                        style={{
                            borderRadius: 12,
                            background: cardBackground,
                            borderColor,
                        }}
                    >

                        <Section
                            title="Topic Content"
                            value={topic.content}
                        />


                        <Section
                            title="Learning Objectives"
                            value={
                                topic.learning_objectives
                            }
                        />


                        <Section
                            title="Learning Outcomes"
                            value={
                                topic.learning_outcomes
                            }
                        />


                        <Section
                            title="Competencies"
                            value={
                                topic.competencies
                            }
                        />

                    </Card>

                </Col>


                {/* --------------------------------------------------------- */}
                {/* TEACHING GUIDANCE */}
                {/* --------------------------------------------------------- */}

                <Col
                    xs={24}
                    lg={8}
                >

                    <Card
                        title="Teaching Guidance"
                        style={{
                            borderRadius: 12,
                            background: cardBackground,
                            borderColor,
                        }}
                    >

                        <Section
                            title="Suggested Activities"
                            value={
                                topic.suggested_activities
                            }
                        />


                        <Section
                            title="Assessment Guidance"
                            value={
                                topic.assessment_guidance
                            }
                        />


                        <Section
                            title="Teaching Resources"
                            value={
                                topic.teaching_resources
                            }
                        />


                        <Section
                            title="References"
                            value={
                                topic.references
                            }
                        />

                    </Card>

                </Col>

            </Row>

        </div>
    );
};


/* ==========================================================================
   SECTION
   ========================================================================== */

const Section = ({
    title,
    value,
}) => {

    if (!value) {
        return null;
    }


    return (

        <div
            style={{
                marginBottom: 22,
            }}
        >

            <Title
                level={5}
                style={{
                    marginBottom: 8,
                }}
            >
                {title}
            </Title>


            <Paragraph
                style={{
                    whiteSpace: "pre-wrap",
                    marginBottom: 0,
                }}
            >
                {value}
            </Paragraph>

        </div>

    );
};


/* ==========================================================================
   SPACE BLOCK
   ========================================================================== */

const SpaceBlock = ({
    children,
}) => {

    return (

        <div
            style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
            }}
        >
            {children}
        </div>

    );
};


export default CurriculumTopicDetails;

